import React, { useCallback, useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useSelector } from 'react-redux';

import { Formik, Form } from 'formik';
import { Button, Grid, Header, Popup } from 'semantic-ui-react';
import useMediaUpload from '../../../hooks/useMediaUpload';
import { litGroupValidationSchema } from '../../utils/validationSchemas';
import { useLitGroupOperationsZoneBased } from '../../hooks/entities/useLitGroupOperationsZoneBased';
import {
  // FormikInput,
  // FormikTextArea,
  // FormikSelect,
  // FormikField,
  FormChangeDetector,
  FormikImageUploadAdapted

} from '../common';

// import {
//   // FormikInput,
//   // FormikTextArea,
//   FormikSelect,
//   // FormikField
// } from '../../../components/FormComponents';

import {
  FormikInput,
  FormikTextArea,
  FormikSelect,
  FormikField
} from '../../../pages/subPages/voiceActor/FormComponents';
// import './VoiceActorProfileForm.css'; // Reuse the same CSS

import { allStates, literaryGroupTypes } from '../../../constants/config';
import { SimpleValuesDisplay } from '../../../utils/aaiProfileTools';
import ShowStatusMessages from '../common/StatusMessageDisplay';

import '../../../components/VoiceActorProfileForm.css'; // Reuse the same CSS

/**
 * LitGroupProfileForm component
 * Form for creating and editing literary group profiles
 * @param {Object} props - Component props
 * @param {Function} props.onSave - Callback when the form is saved
 * @param {Object} props.selectedLitGroupProfile - The selected literary group profile data
 * @param {string} props.litGroupId - The selected literary group ID
 * @returns {JSX.Element} LitGroupProfileForm component
 */
const LitGroupProfileForm = forwardRef(({ onSave, selectedLitGroupProfile, litGroupId }, ref) => {
  // Get the real functions from the zone-based hook
  const { createLitGroup, updateLitGroup } = useLitGroupOperationsZoneBased();

  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [formChanged, setFormChanged] = useState(false);
  const formikRef = useRef();
  const imageUploadRef = useRef();
  const userProfile = useSelector((state) => state.user.profile);
  const userId = userProfile?.data?.userId || 'default-user-id';
  // const [isUploading, setIsUploading] = useState(false);
  // const [uploadError, setUploadError] = useState(false);
  // const { uploadMedia, isUploading, error: uploadError, reset: resetUpload } = useMediaUpload();
  const { uploadMedia, isUploading: isAvatarUploading, error: avatarUploadError } = useMediaUpload();

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);


  useEffect(() => {
    console.log('selectedLitGroupProfile', selectedLitGroupProfile);
  }, [selectedLitGroupProfile]);

  // Form submission handler
  // In LitGroupProfileForm.jsx handleSubmit function
  const handleSubmit = async (values) => {
    setFormLoading(true);
    setErrorMessage(null);
    setSuccessMessage('');
    console.log('Form values before submission:', values);

    try {
      let result;
      let updatedValues = { ...values };

      // If we have a new image file, upload it first
      if (values.hasNewImageFile && values.profileImageFile) {
        try {
          console.log('Uploading new image file...');
          // Use the useMediaUpload hook to upload the file
          const uploadUrl = await uploadMedia(
            values.profileImageFile,
            'literaryGroupProfileImage',
            {
              litGroupId: litGroupId,
              resourceType: 'literaryGroupProfileImage'
            },
            userId
          );

          console.log('Image uploaded successfully, URL:', uploadUrl);
          // Update the profileImageUrl with the new S3 URL
          updatedValues.profileImageUrl = uploadUrl;
        } catch (err) {
          console.error('Error uploading image:', err);
          setErrorMessage('Failed to upload image: ' + err.message);
          setFormLoading(false);
          return;
        }
      }

      // Clean the values by removing frontend-only fields before sending to server
      const cleanedValues = { ...updatedValues };
      delete cleanedValues.profileImageFile;  // Remove file object
      delete cleanedValues.hasNewImageFile;   // Remove frontend flag
      delete cleanedValues._formDirty;        // Remove frontend state tracker
      delete cleanedValues._forceUpdate;      // Remove frontend state tracker

      if (litGroupId && selectedLitGroupProfile) {
        // Update existing literary group with the cleaned values
        console.log('Updating with cleaned values:', cleanedValues);
        result = await updateLitGroup({
          litGroupId,
          data: cleanedValues
        });
        setSuccessMessage('Literary group updated successfully!');
      } else {
        // Create new literary group with the pre-generated UUID
        console.log('Creating new literary group with UUID:', litGroupId);
        result = await createLitGroup(cleanedValues, litGroupId);
        setSuccessMessage('Literary group created successfully!');
      }

      setFormChanged(false);

      // Call the onSave callback if provided
      if (onSave) {
        onSave(result);
      }
    } catch (err) {
      setErrorMessage(err.message || 'An error occurred while saving the literary group');
    } finally {
      setFormLoading(false);
    }
  };

  // Reset form to initial values
  // const handleReset = () => {
  //   if (formikRef.current) {
  //     formikRef.current.resetForm();
  //     setFormChanged(false);
  //   }
  // };

  // Get initial values for the form
  // const getInitialValues = () => {
  //   return {
  //     name: selectedLitGroup?.name || '',
  //     description: selectedLitGroup?.description || '',
  //     type: selectedLitGroup?.type || 'series',
  //     status: selectedLitGroup?.status || 'active'
  //   };
  // };
  const getFormValues = useCallback((data, getType) => {
    const currentData =
    {
      // Literary Group specific fields
      name: data?.name || '',
      description: data?.description || '',
      lgType: data?.lgType || '',
      bookSeries: data?.bookSeries || '',
      authors: Array.isArray(data?.authors) ? data.authors.join(', ') : (typeof data?.authors === 'string' ? data.authors : ''),


      // Voice Actor Profile fields
      firstName: data?.firstName || '',
      lastName: data?.lastName || '',
      street: data?.street || '',
      city: data?.city || '',
      state: data?.state || '',
      zipCode: data?.zipCode || data?.zipcode || '',
      // phones: data?.phones?.join(', ') || '',
      phones: Array.isArray(data?.phones) ? data.phones.join(', ') : (typeof data?.phones === 'string' ? data.phones : ''),

      actorType: data?.actorType || 'Authorator-ai',

      // Use profileImageUrl consistently
      profileImageUrl: data?.profileImageUrl || '',
      profileImageFile: null,
      hasNewImageFile: false
    };
    return currentData;
  }, []);

  // Check if we have profile data
  // const hasProfileData = !!selectedLitGroupProfile;
  const formReset = useCallback((resetData) => {
    if (formikRef.current) {
      console.log('Resetting form with data:', resetData);

      formikRef.current.resetForm({
        values: getFormValues(resetData)
      });

      // Reset form changed state
      setFormChanged(false);
    }
  }, [getFormValues]);

  // Expose the clearProfileImage function via ref
  useImperativeHandle(ref, () => ({
    clearProfileImage: () => {
      if (imageUploadRef.current) {
        imageUploadRef.current.clearImage();
      }
    }
  }), []);

  return (
    <div className="-min" >
      <Header as="h3">
        {litGroupId ? 'Edit Literary Group' : 'Create New Literary Group'}
      </Header>

      {/* {!hasProfileData && litGroupId || 1 === 1 && (
        <Message negative>
          <Message.Header>Profile Data Not Available</Message.Header>
          <p>The profile data for this literary group could not be loaded. Please try selecting the literary group again.</p>
        </Message>
      )} */}

      {(litGroupId || !selectedLitGroupProfile) && (
        <Formik
          innerRef={formikRef}
          initialValues={getFormValues(selectedLitGroupProfile, 'Fromik initialValues')}
          validationSchema={litGroupValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values, isValid, dirty }) => (
            <Form>
              <FormChangeDetector
                onChange={setFormChanged}
                initialValues={getFormValues(selectedLitGroupProfile, 'FormChangeDetector', values)}
              />
              <div className="lgpf-profile-img">
                {userProfile?.data?.userId && (
                  <FormikImageUploadAdapted
                    ref={imageUploadRef}
                    urlFieldName="profileImageUrl"
                    fileFieldName="profileImageFile"
                    flagFieldName="hasNewImageFile"
                    resourceType="literaryGroupProfileImage"
                    label="Profile Picture"
                    hideInputFields={true}
                  />
                )}
              </div>
              <Grid divided={false}>

                <Grid.Row className=''>
                  <Grid.Column width={8}>
                    <FormikField
                      name="name"
                      component={FormikInput}
                      label="Literary Group Name"
                      placeholder="Enter literary group name"
                      fluid
                      value={values.name}
                      style={{ background: 'transparent' }}

                    />
                  </Grid.Column>
                  <Grid.Column width={5} className="noLeftPad">
                    <FormikField
                      name="lgType"
                      component={FormikSelect}
                      label="Literary Group Type"
                      options={literaryGroupTypes.map(type => ({
                        key: type.value,
                        text: type.label,
                        value: type.label
                      }))}
                      fluid
                      placeholder="Literary group type"
                      className="transparent-dropdown"
                      style={{ zIndex: "1002" }}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="noTopBottomPad">
                  <Grid.Column width={8}>
                    <FormikField
                      name="bookSeries"
                      component={FormikInput}
                      label="Book Series"
                      fluid
                      placeholder="Enter book series name"
                    />
                  </Grid.Column>
                  <Grid.Column width={8} className="noLeftPad">
                    <FormikField
                      name="authors"
                      component={FormikInput}
                      label="Authors (comma-separated)"
                      fluid
                      placeholder="e.g. John Smith, Jane Doe"
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <Header as="h4" dividing>Basic Information</Header>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="noTopBottomPad">
                  <Grid.Column className="" width={6}>
                    <FormikField
                      name="firstName"
                      component={FormikInput}
                      label="First Name"
                      fluid
                      placeholder="First Name"
                    />
                  </Grid.Column>
                  <Grid.Column className="noLeftPad" width={6}>
                    <FormikField
                      name="lastName"
                      component={FormikInput}
                      label="Last Name"
                      fluid
                      placeholder="Last Name"
                    />
                  </Grid.Column>
                  <Grid.Column className="noLeftPad" width={4}>
                    <FormikField
                      name="actorType"
                      component={FormikSelect}
                      label="Type"
                      options={[
                        { key: 'authorator', text: 'Authorator-ai', value: 'Authorator-ai' },
                        { key: 'freelance', text: 'Freelance', value: 'Freelance' }
                      ]}
                      fluid
                      placeholder="Select Type"
                      className="transparent-dropdown"
                      style={{ zIndex: "1001" }}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row className='noTopPad'>
                  <Grid.Column width={16}>
                    <Header as="h4" dividing>Contact Information</Header>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className='noTopBottomPad'>
                  <Grid.Column width={16}>
                    <FormikField
                      name="street"
                      component={FormikInput}
                      label="Street Address"
                      fluid
                      placeholder="123 Main St"
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className='noTopBottomPad'>
                  <Grid.Column width={6}>
                    <FormikField
                      name="city"
                      component={FormikInput}
                      label="City"
                      fluid
                      placeholder="City"
                    />
                  </Grid.Column>
                  <Grid.Column width={5}>
                    <FormikField
                      name="state"
                      component={FormikSelect}
                      label="State"
                      options={allStates}
                      fluid
                      placeholder="State"
                      className="transparent-dropdown"
                    />
                  </Grid.Column>
                  <Grid.Column width={5}>
                    <FormikField
                      name="zipCode"
                      component={FormikInput}
                      label="Zip Code"
                      fluid
                      placeholder="12345"

                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className='noTopBottomPad'>
                  <Grid.Column width={16}>
                    <FormikField
                      name="phones"
                      component={FormikInput}
                      label="Phone Numbers (comma-separated)"
                      fluid
                      placeholder="e.g. 555-123-4567, 555-987-6543"
                    />
                  </Grid.Column>
                </Grid.Row>

                {/* Profile Information */}
                <Grid.Row className=''>
                  <Grid.Column width={16}>
                    <Header as="h4" dividing>Profile Information</Header>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="">
                  <Grid.Column width={16}>
                    <FormikField
                      className=""
                      name="description"
                      component={FormikTextArea}
                      label="Description"
                      placeholder="Enter a description for this literary group"
                      rows={4}
                      value={values.description}
                    />
                  </Grid.Column>
                </Grid.Row>

                {/* <Grid.Row className="noTopBottomPad marginTop_10 maxImageEntryRowWidth">
                  <Grid.Column className="" width={16}>
                    {userProfile?.data?.userId && (
                      <FormikImageUploadAdapted
                        urlFieldName="profileImageUrl"
                        fileFieldName="profileImageFile"
                        flagFieldName="hasNewImageFile"
                        resourceType="literaryGroupProfileImage"
                        label="Profile Picture"
                      />

                      // <FormikImageUploadAdapted
                      //   urlFieldName="profileImageUrl"
                      //   fileFieldName="profileImageFile"
                      //   flagFieldName="hasNewImageFile"
                      //   label="Profile Picture"
                      //   resourceType="literaryGroupProfileImage"
                      //   onImageChange={(file) => {
                      //     // Set the profileImageFile field
                      //     formikRef.current.setFieldValue('profileImageFile', file);
                      //     // Set the hasNewImageFile flag
                      //     formikRef.current.setFieldValue('hasNewImageFile', true);
                      //   }}
                      // />
                    )}
                  </Grid.Column>
                </Grid.Row> */}
                {/* Status messages */}
                <ShowStatusMessages
                  isAvatarUploading={isAvatarUploading}
                  avatarUploadError={avatarUploadError}
                  successMessage={successMessage}
                  errorMessage={errorMessage}
                />
                {/* {(isAvatarUploading || avatarUploadError || successMessage || error) && (
                  <Grid.Row>
                    <Grid.Column className="fiveTopBottomPad" width={16}>
                      {isAvatarUploading && (
                        <Message className="marginTop_5 noBottomMargin fiveTopBottomPad" info>
                          <Icon name="spinner" loading />
                          <span>Uploading image...</span>
                        </Message>
                      )}

                      {avatarUploadError && (
                        <Message className="marginTop_5 noBottomMargin fiveTopBottomPad" negative>
                          <Message.Header>Upload Error</Message.Header>
                          <p>{avatarUploadError.userMessage || avatarUploadError.message || String(avatarUploadError)}</p>
                        </Message>
                      )}

                      {error && (
                        <Message className="marginTop_5 noBottomMargin fiveTopBottomPad" negative>
                          <Message.Header>Error</Message.Header>
                          <p>{typeof error === 'string'
                            ? error
                            : error.userMessage || error.message || JSON.stringify(error)}</p>
                        </Message>
                      )}

                      {successMessage && (
                        <Message className="marginTop_5 fiveTopBottomPad" positive>
                          <Message.Header>Success</Message.Header>
                          <p>{successMessage}</p>
                        </Message>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                )} */}

                {/* Form buttons */}
                <Grid.Row className="noTopPad">
                  <Grid.Column width={16}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Popup
                        className='values-display-popup'
                        content={<SimpleValuesDisplay key={Math.random().toString()} values={values} isFormChanged={formChanged} />}
                        trigger={
                          <span>
                            <Button
                              primary
                              type="submit" // Changed from "submit" to "button"
                              loading={formLoading}
                              disabled={litGroupId ? !formChanged : false} // For new groups, always enabled; for existing, require changes
                              onClick={() => {
                                console.log('Submit button clicked, form state:', {
                                  isSubmitting,
                                  isValid,
                                  dirty,
                                  formChanged
                                });
                                handleSubmit(formikRef.current.values);
                                // formikRef.current.submitForm();
                              }}
                            >
                              {litGroupId ? 'Update Literary Group' : 'Create Literary Group'}
                            </Button>
                          </span>
                        }
                        position="top left"
                        size="tiny"
                        offset={[0, 10]}
                      />

                      {/* Show Cancel button for new groups or when changes have been made for existing groups */}
                      {(!litGroupId || formChanged) && (
                        <Popup
                          content={litGroupId ? "Discard all changes and reset the form" : "Clear the form"}
                          trigger={
                            <Button
                              type="button"
                              onClick={() => {
                                if (litGroupId) {
                                  // Reset to the profile's values for existing groups
                                  formReset(selectedLitGroupProfile);
                                } else {
                                  // Clear the form for new groups
                                  formReset(null);
                                }
                              }}
                            >
                              {litGroupId ? 'Cancel Changes' : 'Clear Form'}
                            </Button>
                          }
                          position="top left"
                          size="tiny"
                        />
                      )}
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
});

export default LitGroupProfileForm;
